import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Sparkle,
  ShieldCheck,
  Wallet,
  Clock,
  ChatCircleDots,
  Camera,
  Scales,
  CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import GoldRateTicker from "@/components/GoldRateTicker";

const SUGGESTIONS = [
  "Is today good to buy gold?",
  "What is 916 BIS hallmark?",
  "What suits oval face?",
  "Fair price for 10g chain?",
];

export default function Home() {
  const [rates, setRates] = useState(null);
  const [draft, setDraft] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/gold-rates").then((r) => setRates(r.data.rates)).catch(() => {});
  }, []);

  const sendQuestion = (q) => {
    const text = (q || draft).trim();
    if (!text) return;
    sessionStorage.setItem("mineai_pending_msg", text);
    navigate("/chat");
  };

  const [waitForm, setWaitForm] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [waitLoading, setWaitLoading] = useState(false);

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!waitForm.name || !waitForm.email || !waitForm.phone) {
      toast.error("Please fill all fields");
      return;
    }
    setWaitLoading(true);
    try {
      await api.post("/waitlist", { ...waitForm, user_type: "buyer" });
      toast.success("You're on the list! We'll be in touch.");
      setWaitForm({ name: "", email: "", phone: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setWaitLoading(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E8DFD1] text-xs uppercase tracking-[0.2em] text-[#4A5568]">
                <Sparkle size={14} weight="fill" className="text-[#D4AF37]" />
                Auromine · Chennai
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] tracking-tight text-[#0A1128]">
                Your personal{" "}
                <span className="gold-text">jewellery expert</span>,
                <br className="hidden sm:block" />
                in your pocket.
              </h1>
              <p className="mt-5 text-base sm:text-lg text-[#4A5568] max-w-xl leading-relaxed">
                Ask Mine AI anything about gold, hallmarks, and fair prices —
                get honest answers instantly. Built to help you buy smart and
                save more.
              </p>

              {/* Big chat input */}
              <div
                className="mt-7 mc-card p-3 sm:p-4 flex items-center gap-2 sm:gap-3 shadow-sm"
                data-testid="hero-chat-input-wrap"
              >
                <ChatCircleDots
                  size={22}
                  weight="duotone"
                  className="text-[#3B5BDB] ml-2 hidden sm:block"
                />
                <input
                  data-testid="hero-chat-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendQuestion()}
                  placeholder="Ask me anything... Is this gold price fair? What suits my face?"
                  className="flex-1 bg-transparent outline-none text-base px-2 py-2 placeholder:text-[#4A5568]/60"
                />
                <button
                  data-testid="hero-chat-send"
                  onClick={() => sendQuestion()}
                  className="px-5 py-3 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth flex items-center gap-2"
                >
                  Ask <ArrowRight size={16} weight="bold" />
                </button>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={s}
                    data-testid={`suggestion-chip-${i}`}
                    onClick={() => sendQuestion(s)}
                    className="px-3 py-2 rounded-full bg-white border border-[#E8DFD1] text-xs text-[#0A1128]/80 hover:border-[#3B5BDB] hover:text-[#3B5BDB] smooth"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 fade-up" style={{ animationDelay: "150ms" }}>
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-tr from-[#D4AF37]/20 to-[#3B5BDB]/10 rounded-3xl blur-2xl" />
                <img
                  src="https://images.pexels.com/photos/30276931/pexels-photo-30276931.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Gold jewellery"
                  className="relative rounded-3xl w-full h-[420px] object-cover border border-[#E8DFD1]"
                  data-testid="hero-image"
                />
                <div className="absolute -bottom-5 left-5 right-5 mc-card p-4 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#3B5BDB]/10 flex items-center justify-center">
                    <ShieldCheck size={22} weight="duotone" className="text-[#3B5BDB]" />
                  </div>
                  <div className="text-sm">
                    <p className="font-medium text-[#0A1128]">
                      Trusted advice, never an upsell
                    </p>
                    <p className="text-xs text-[#4A5568]">
                      We side with your wallet, not the shop.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <GoldRateTicker rates={rates} />
      </section>

      {/* VALUE CARDS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="grid md:grid-cols-3 gap-5 lg:gap-7">
          {[
            {
              icon: Wallet,
              title: "Save Money",
              text: "Know when prices are fair. Spot overcharging instantly with full price breakdowns.",
              tone: "text-[#16A34A]",
            },
            {
              icon: Clock,
              title: "Save Time",
              text: "Get expert jewellery advice in seconds, not hours. No appointments needed.",
              tone: "text-[#3B5BDB]",
            },
            {
              icon: ShieldCheck,
              title: "Buy Confidently",
              text: "Verify hallmarks, check authenticity, and walk into any shop fully prepared.",
              tone: "text-[#D4AF37]",
            },
          ].map((v, i) => (
            <div
              key={v.title}
              className="mc-card lift smooth p-7"
              data-testid={`value-card-${i}`}
            >
              <v.icon size={32} weight="duotone" className={v.tone} />
              <h3 className="mt-4 font-display text-2xl text-[#0A1128]">
                {v.title}
              </h3>
              <p className="mt-2 text-sm text-[#4A5568] leading-relaxed">
                {v.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#F5EFE6]/50 border-y border-[#E8DFD1]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
            How it works
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 max-w-2xl">
            Three steps. Zero pressure.
          </h2>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              {
                n: "01",
                title: "Ask Mine AI your question",
                text: "Type, talk, or send a photo. In Tamil, Hindi, Telugu, or English.",
                Icon: ChatCircleDots,
              },
              {
                n: "02",
                title: "Get instant expert advice",
                text: "Honest answers with full price breakdowns and red-flag warnings.",
                Icon: Camera,
              },
              {
                n: "03",
                title: "Buy smart, save more",
                text: "Walk into the shop already knowing the fair price.",
                Icon: Scales,
              },
            ].map((s, i) => (
              <div
                key={s.n}
                className="bg-white rounded-2xl p-7 border border-[#E8DFD1]"
                data-testid={`how-step-${i}`}
              >
                <span className="font-display text-3xl text-[#D4AF37]">{s.n}</span>
                <s.Icon size={28} weight="duotone" className="text-[#3B5BDB] mt-4" />
                <h3 className="font-display text-xl mt-3 text-[#0A1128]">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-[#4A5568]">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
              Early access
            </p>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[#0A1128]">
              Be first in line when Mine AI launches
            </h2>
            <p className="mt-3 text-[#4A5568]">
              Join the waitlist and we'll send you early access plus a free
              jewellery buying guide.
            </p>
            <div className="mt-5 flex items-center gap-2 text-sm text-[#4A5568]">
              <CheckCircle size={18} weight="duotone" className="text-[#16A34A]" />
              No spam · Unsubscribe anytime
            </div>
          </div>
          <form
            onSubmit={submitWaitlist}
            className="mc-card p-6 sm:p-8 space-y-4"
            data-testid="waitlist-form"
          >
            <div>
              <label className="text-xs text-[#4A5568] uppercase tracking-wider">
                Full name
              </label>
              <input
                data-testid="waitlist-name"
                value={waitForm.name}
                onChange={(e) => setWaitForm({ ...waitForm, name: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] smooth"
                placeholder="Lakshmi Iyer"
              />
            </div>
            <div>
              <label className="text-xs text-[#4A5568] uppercase tracking-wider">
                Email
              </label>
              <input
                data-testid="waitlist-email"
                type="email"
                value={waitForm.email}
                onChange={(e) => setWaitForm({ ...waitForm, email: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] smooth"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="text-xs text-[#4A5568] uppercase tracking-wider">
                Phone
              </label>
              <input
                data-testid="waitlist-phone"
                value={waitForm.phone}
                onChange={(e) => setWaitForm({ ...waitForm, phone: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] focus:ring-1 focus:ring-[#3B5BDB] smooth"
                placeholder="+91 98xxxxxxxx"
              />
            </div>
            <button
              data-testid="waitlist-submit"
              type="submit"
              disabled={waitLoading}
              className="w-full py-3 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth disabled:opacity-60"
            >
              {waitLoading ? "Joining..." : "Join the Waitlist"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
